import useLocalization from "@/hooks/useLocalization";
import icons from "@/tools/icons";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { Box, Button, IconButton, Stack } from "@mui/material";
import { addMonths, format, subMonths } from "date-fns";
import YearMonthButton from "./YearMonthButton";

type ReservationCalendarHeaderProps = {
  displayedMonth: Date;
  isMobile: boolean;
  minMonthStart: Date | null;
  maxMonthStart: Date | null;
  isPrevDisabled: boolean;
  isNextDisabled: boolean;
  onDateClick?: (date: string) => void;
  onMonthChange: (month: Date) => void;
};

export default function ReservationCalendarHeader({
  displayedMonth,
  isMobile,
  minMonthStart,
  maxMonthStart,
  isPrevDisabled,
  isNextDisabled,
  onDateClick,
  onMonthChange,
}: ReservationCalendarHeaderProps) {
  const { t } = useLocalization();

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "560px",
        marginX: isMobile ? "auto" : 0,
        paddingTop: isMobile ? 1 : 0,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent={isMobile ? "center" : "flex-start"}
        sx={{ flexWrap: "nowrap", gap: 1 }}
      >
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          onClick={() => {
            const today = new Date();
            let nextDate = today;
            if (minMonthStart && nextDate.getTime() < minMonthStart.getTime()) {
              nextDate = minMonthStart;
            }
            if (maxMonthStart && nextDate.getTime() > maxMonthStart.getTime()) {
              nextDate = maxMonthStart;
            }
            onMonthChange(nextDate);
            onDateClick?.(format(nextDate, "yyyy-MM-dd"));
          }}
          sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
          endIcon={<icons.Calendar fontSize="inherit" />}
        >
          {t("date:today")}
        </Button>
        <Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
          <IconButton
            aria-label="Previous month"
            onClick={() => {
              if (isPrevDisabled) return;
              onMonthChange(subMonths(displayedMonth, 1));
            }}
            disabled={isPrevDisabled}
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            aria-label="Next month"
            onClick={() => {
              if (isNextDisabled) return;
              onMonthChange(addMonths(displayedMonth, 1));
            }}
            disabled={isNextDisabled}
          >
            <ChevronRight />
          </IconButton>
        </Stack>
        <YearMonthButton
          displayedMonth={displayedMonth}
          isMobile={isMobile}
          minMonthStart={minMonthStart}
          maxMonthStart={maxMonthStart}
          onMonthChange={onMonthChange}
        />
      </Stack>
    </Box>
  );
}
